import { useState, useEffect, useCallback } from 'react'
import { sightingAPI } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { createLogger } from '../utils/logger'

const logger = createLogger('useEnrichmentNudges')
const STORAGE_KEY = 'trckit:dismissed_enrichment_nudges'

function getDismissed() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
  } catch {
    return []
  }
}

function saveDismissed(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
  } catch {
    // ignore storage errors
  }
}

/**
 * useEnrichmentNudges - Fetches recent sightings with no review or offering yet.
 * Returns nudge items for display in ProximityNotificationStack.
 * Dismissed nudges are persisted to localStorage and never re-shown.
 *
 * @returns {{ nudges: Array, dismiss: Function, loading: boolean }}
 */
export function useEnrichmentNudges() {
  const { isAuthenticated } = useAuth()
  const [all, setAll] = useState([])
  const [dismissed, setDismissed] = useState(getDismissed)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) return
    setLoading(true)
    sightingAPI.getUnenrichedSightings()
      .then(r => setAll(r.data?.sightings || []))
      .catch(err => logger.warn('Failed to load enrichment nudges', err))
      .finally(() => setLoading(false))
  }, [isAuthenticated])

  const dismiss = useCallback((sightingId) => {
    setDismissed(prev => {
      const next = [...prev, sightingId]
      saveDismissed(next)
      return next
    })
  }, [])

  const nudges = all.filter(n => !dismissed.includes(n.sightingId))

  return { nudges, dismiss, loading }
}
